"use client";

import React from "react";
import { CircleAlert as AlertTriangle, Droplets, Activity, Gauge, Wind, Flame, PhoneCall, ArrowRight, ShieldAlert } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
interface WarningSign {
  id: number;
  title: string;
  category: string;
  description: string;
  actionAdvice: string;
  severity: "High" | "Critical" | "Moderate";
  iconName: string;
}
const WARNING_SIGNS: WarningSign[] = [{
  id: 1,
  title: "Unexplained Spike in Water Bills",
  category: "Hidden Flow",
  description: "A sudden jump in your monthly municipal water bill with no change in household usage almost always points to an undetected continuous leak behind walls or underground.",
  actionAdvice: "Check your meter dial; if it spins when no fixtures run, water is escaping.",
  severity: "Critical",
  iconName: "Activity"
}, {
  id: 2,
  title: "Damp Spots, Wall Stains & Peeling Paint",
  category: "Structural Warning",
  description: "Discoloration on drywall, bubbling paint, warped baseboards, or squishy flooring indicate sustained moisture accumulation inside cavity walls or subfloors.",
  actionAdvice: "Do not puncture bubbling areas; shut off the nearest fixture valve immediately.",
  severity: "Critical",
  iconName: "Droplets"
}, {
  id: 3,
  title: "Persistent Musty or Earthy Odors",
  category: "Air Quality & Mold",
  description: "Mold spores thrive in dark, humid enclosed spaces within 24 to 48 hours of pipe failure, giving off an unmistakable sour damp smell in bathrooms, closets, or basements.",
  actionAdvice: "Pinpoint whether the scent intensifies near specific fixtures or lower baseboards.",
  severity: "High",
  iconName: "Wind"
}, {
  id: 4,
  title: "Warm Spots on Concrete Floors",
  category: "Slab Leak Symptom",
  description: "Unusual warm patches on tile, hardwood, or carpeted ground-floor areas often mean hot water copper supply lines are fractured beneath the concrete foundation slab.",
  actionAdvice: "Avoid walking barefoot on overheating spots and call for acoustic slab location.",
  severity: "Critical",
  iconName: "Flame"
}, {
  id: 5,
  title: "Unexplained Drop in Water Pressure",
  category: "System Pressure",
  description: "When multiple showers or faucets simultaneously lose flow volume without a neighborhood outage, water may be escaping through a ruptured main or cracked riser.",
  actionAdvice: "Inspect outdoor hose bibbs and crawl spaces for pooled groundwater.",
  severity: "High",
  iconName: "Gauge"
}, {
  id: 6,
  title: "Sounds of Running Water Behind Walls",
  category: "Acoustic Sign",
  description: "Hearing faint hissing, dripping, or rushing sounds inside wall partitions when every toilet, faucet, and appliance is turned off is a clear sign of an active pressurized leak.",
  actionAdvice: "Turn off your main emergency shut-off valve to avoid structural flooding.",
  severity: "Moderate",
  iconName: "AlertTriangle"
}];
export default function LeakWarnings() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Activity":
        return <Activity className="size-5 text-destructive" />;
      case "Droplets":
        return <Droplets className="size-5 text-destructive" />;
      case "Wind":
        return <Wind className="size-5 text-destructive" />;
      case "Flame":
        return <Flame className="size-5 text-destructive" />;
      case "Gauge":
        return <Gauge className="size-5 text-destructive" />;
      default:
        return <AlertTriangle className="size-5 text-destructive" />;
    }
  };
  return <section id="leakwarningsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            Early Leak Detection
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Warning Signs of a Hidden or Active Water Leak
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Catching water leaks before they cause major foundation erosion or structural mold can save thousands in emergency remediation. Inspect your Jonesboro home for these common symptoms.
          </p>
        </div>

        {/* Emergency Alert Banner */}
        <div className="mb-12">
          <Alert className="border-destructive/30 bg-destructive/5 text-foreground flex items-start gap-4 p-4 md:p-6 rounded-xl shadow-xs">
            <ShieldAlert className="size-6 text-destructive shrink-0 mt-0.5" />
            <div className="flex flex-col md:flex-row md:items-center justify-between w-full gap-4">
              <div>
                <AlertTitle className="text-base font-semibold text-foreground mb-1">
                  Hearing continuous water rushing or seeing drywall bulge right now?
                </AlertTitle>
                <AlertDescription className="text-sm text-muted-foreground">
                  Turn off your home main water shut-off valve immediately to limit structural damage, then contact our 24/7 emergency dispatch team.
                </AlertDescription>
              </div>
              <div className="shrink-0 flex items-center gap-3">
                <a href="tel:4708479076" className="inline-flex items-center">
                  <Button variant="destructive" size="default" className="font-semibold shadow-sm">
                    <PhoneCall className="size-4 mr-2" />
                    <span>Call Emergency Tech</span>
                  </Button>
                </a>
              </div>
            </div>
          </Alert>
        </div>

        {/* Grid of Leak Warning Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {WARNING_SIGNS.map((sign, index) => <motion.div key={sign.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 rounded-xl">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="p-2.5 rounded-lg bg-destructive/10 inline-flex items-center justify-center">
                        {getIcon(sign.iconName)}
                      </div>
                      <Badge variant={sign.severity === "Critical" ? "destructive" : "secondary"} className="text-xs font-semibold px-2.5 py-0.5">
                        {sign.severity} Priority
                      </Badge>
                    </div>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground block mb-1">
                      {sign.category}
                    </span>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground leading-snug">
                      {sign.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0 pb-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {sign.description}
                    </CardDescription>

                    <div className="p-3 bg-muted/60 rounded-lg border border-border/70 text-xs text-foreground/90">
                      <span className="font-semibold text-foreground block mb-1">Recommended Action:</span>
                      {sign.actionAdvice}
                    </div>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2 border-t border-border/40 mt-auto flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Jonesboro Emergency Plumber</span>
                  <Link to="/contact/" className="inline-flex items-center text-xs font-semibold text-primary hover:underline group">
                    <span>Schedule Check</span>
                    <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA & Guidance Box */}
        <div className="mt-14 bg-secondary/60 border border-border rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="max-w-2xl text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold font-default text-secondary-foreground mb-2">
              Unsure if you have a slab leak or pipe breach?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Our technicians use non-invasive acoustic sensors and thermal imaging to locate exact leak spots without tearing down your walls unnecessarily.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Link to="/problems/">
              <Button variant="outline" size="lg" className="font-medium">
                View Common Problems
              </Button>
            </Link>
            <Link to="/contact/">
              <Button variant="default" size="lg" className="font-semibold shadow-xs">
                Book Leak Inspection
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}